import { useMemo } from 'react';
import { TotemGallery } from 'components/TotemGallery';
import {
    GetDeliveryQuery,
    GetDailyDeliveriesWithPhotosQuery,
    PhotosInstructionFragmentFragment,
    PhotoState,
    ControlPhotoFragmentFragment,
    PhotoIssueTypeInput,
} from 'data/__generated__';
import { isPhotosInstruction } from 'pages/SiteSetups/utils/typePredicates';
import { separatePhotosByStartEnd } from 'pages/Deliveries/utils/photoUtils';
import styled from 'styled-components';

type DeliveryWithPhotos =
    | NonNullable<GetDeliveryQuery['delivery']>
    | NonNullable<GetDailyDeliveriesWithPhotosQuery['deliveries']>[0];

export const getDeliveryPhotos = (
    delivery: DeliveryWithPhotos,
): {
    storePhotos: ControlPhotoFragmentFragment[];
    freefoodPhotos: ControlPhotoFragmentFragment[];
    storeStartPhotos: ControlPhotoFragmentFragment[];
    storeEndPhotos: ControlPhotoFragmentFragment[];
    freefoodStartPhotos: ControlPhotoFragmentFragment[];
    freefoodEndPhotos: ControlPhotoFragmentFragment[];
} => {
    const storePhotosInstructions =
        (delivery.siteSetup?.microstoreColumnGroupSetups
            ?.flatMap(({ instructionsAfterColumns, instructionsBeforeColumns, microstoreColumnSetups }) => [
                ...instructionsBeforeColumns,
                ...microstoreColumnSetups.flatMap(({ instructions }) => instructions),
                ...instructionsAfterColumns,
            ])
            ?.filter((instruction) =>
                isPhotosInstruction(instruction as PhotosInstructionFragmentFragment),
            ) as PhotosInstructionFragmentFragment[]) ?? [];

    // A delivery with no site has no SiteSetup: its freefood rangement hangs on the delivery itself.
    const freefoodPhotosInstructions =
        ((delivery.siteSetup?.freefoodSetup ?? delivery.freefoodSetup)?.instructions?.filter((instruction) =>
            isPhotosInstruction(instruction as PhotosInstructionFragmentFragment),
        ) as PhotosInstructionFragmentFragment[]) ?? [];

    const storePhotos = storePhotosInstructions.flatMap((instruction) => instruction.controlPhotos);

    const freefoodPhotos = [
        ...freefoodPhotosInstructions.flatMap((instruction) => instruction.controlPhotos),
        ...delivery.controlPhotos,
    ];

    const { startPhotos: storeStartPhotos, endPhotos: storeEndPhotos } = separatePhotosByStartEnd(storePhotos);
    const { startPhotos: freefoodStartPhotos, endPhotos: freefoodEndPhotos } = separatePhotosByStartEnd(freefoodPhotos);

    return {
        storePhotos,
        freefoodPhotos,
        storeStartPhotos,
        storeEndPhotos,
        freefoodStartPhotos,
        freefoodEndPhotos,
    };
};

export const filterPhotos = (
    controlPhotos: ControlPhotoFragmentFragment[],
    selectedStates: PhotoState[] = [],
    selectedIssueTypes: PhotoIssueTypeInput[] = [],
): ControlPhotoFragmentFragment[] => {
    const filteredByState = controlPhotos.filter((controlPhoto) => {
        if (selectedStates.length === 0) return true;
        const photoState = controlPhoto.state || PhotoState.Pending;
        return selectedStates.includes(photoState);
    });

    const filteredByIssueType = filteredByState.filter((controlPhoto) => {
        if (selectedIssueTypes.length === 0) return true;

        return selectedIssueTypes.every((filter) => {
            if (filter.isBadQuality && !controlPhoto.issueType.isBadQuality) return false;
            if (filter.isBadQuantity && !controlPhoto.issueType.isBadQuantity) return false;
            return true;
        });
    });

    return filteredByIssueType;
};

export const hasPhotos = (delivery: DeliveryWithPhotos): boolean => {
    const { storePhotos, freefoodPhotos } = getDeliveryPhotos(delivery);
    return storePhotos.length > 0 || freefoodPhotos.length > 0;
};

export const hasPhotosAfterFilters = (
    delivery: DeliveryWithPhotos,
    selectedStates: PhotoState[] = [],
    selectedIssueTypes: PhotoIssueTypeInput[] = [],
    showBeforePhotos: boolean = true,
    showAfterPhotos: boolean = true,
): boolean => {
    const { storeStartPhotos, storeEndPhotos, freefoodStartPhotos, freefoodEndPhotos } = getDeliveryPhotos(delivery);

    const filteredStoreStartPhotos = filterPhotos(storeStartPhotos, selectedStates, selectedIssueTypes);
    const filteredStoreEndPhotos = filterPhotos(storeEndPhotos, selectedStates, selectedIssueTypes);
    const filteredFreefoodStartPhotos = filterPhotos(freefoodStartPhotos, selectedStates, selectedIssueTypes);
    const filteredFreefoodEndPhotos = filterPhotos(freefoodEndPhotos, selectedStates, selectedIssueTypes);

    const hasBeforePhotos = filteredStoreStartPhotos.length > 0 || filteredFreefoodStartPhotos.length > 0;
    const hasAfterPhotos = filteredStoreEndPhotos.length > 0 || filteredFreefoodEndPhotos.length > 0;

    return (showBeforePhotos && hasBeforePhotos) || (showAfterPhotos && hasAfterPhotos);
};

export const PhotosSectionContent = ({
    delivery,
    selectedStates = [],
    selectedIssueTypes = [],
    showBeforePhotos = true,
    showAfterPhotos = true,
}: {
    delivery: DeliveryWithPhotos;
    selectedStates?: PhotoState[];
    selectedIssueTypes?: PhotoIssueTypeInput[];
    showBeforePhotos?: boolean;
    showAfterPhotos?: boolean;
}) => {
    const { storeStartPhotos, storeEndPhotos, freefoodStartPhotos, freefoodEndPhotos } = useMemo(
        () => getDeliveryPhotos(delivery),
        [delivery],
    );

    const filteredStoreStartPhotos = useMemo(
        () => filterPhotos(storeStartPhotos, selectedStates, selectedIssueTypes),
        [storeStartPhotos, selectedStates, selectedIssueTypes],
    );

    const filteredStoreEndPhotos = useMemo(
        () => filterPhotos(storeEndPhotos, selectedStates, selectedIssueTypes),
        [storeEndPhotos, selectedStates, selectedIssueTypes],
    );

    const filteredFreefoodStartPhotos = useMemo(
        () => filterPhotos(freefoodStartPhotos, selectedStates, selectedIssueTypes),
        [freefoodStartPhotos, selectedStates, selectedIssueTypes],
    );

    const filteredFreefoodEndPhotos = useMemo(
        () => filterPhotos(freefoodEndPhotos, selectedStates, selectedIssueTypes),
        [freefoodEndPhotos, selectedStates, selectedIssueTypes],
    );

    return (
        <>
            {showBeforePhotos && filteredFreefoodStartPhotos.length > 0 && (
                <>
                    <Title>Photos Freefood - Début du rangement</Title>
                    <TotemGallery images={filteredFreefoodStartPhotos} />
                </>
            )}
            {showAfterPhotos && filteredFreefoodEndPhotos.length > 0 && (
                <>
                    <Title>Photos Freefood - Fin du rangement</Title>
                    <TotemGallery images={filteredFreefoodEndPhotos} />
                </>
            )}
            {showBeforePhotos && filteredStoreStartPhotos.length > 0 && (
                <>
                    <Title>Photos Store - Début du rangement</Title>
                    <TotemGallery images={filteredStoreStartPhotos} />
                </>
            )}
            {showAfterPhotos && filteredStoreEndPhotos.length > 0 && (
                <>
                    <Title>Photos Store - Fin du rangement</Title>
                    <TotemGallery images={filteredStoreEndPhotos} />
                </>
            )}
        </>
    );
};

const Title = styled.div`
    margin-top: 5px;
    font-weight: bold;
    font-size: 18px;
`;
